import { useState, useEffect, useCallback } from 'react';
import { FootballGame } from '@/types';
import { getWorldCupGames } from '@/services/api/football';
import { getWorldCupAllGames, getWorldCupTodayGames } from '@/services/api/espn-football';
import { get365FootballGameDetail, get365FootballGamesList } from '@/services/api/scores365';
import { livePoller } from '@/services/livePoller';
import { sameFootballMatch } from '@/utils/teamMatching';
import { gameCache } from '@/store/gameCache';

export interface WorldCupDay {
  date: string;       // 'YYYY-MM-DD'
  label: string;      // 'Hoje', '18/06', etc.
  games: FootballGame[];
}

function dateKeyFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function localDateKey(iso: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
    return dateKeyFromDate(date);
  } catch {
    return iso.slice(0, 10);
  }
}

function formatDateLabel(dateStr: string): string {
  const now = new Date();
  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(now.getDate() + 1);
  const today = dateKeyFromDate(now);
  const tomorrow = dateKeyFromDate(tomorrowDate);
  if (dateStr === today) return 'Hoje';
  if (dateStr === tomorrow) return 'Amanhã';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function isWorldCupGame(game: FootballGame): boolean {
  const name = game.league.name.toLowerCase();
  return name.includes('world cup') || name.includes('copa do mundo');
}

function isLiveGame(game: FootballGame): boolean {
  return game.status === 'live' || game.status === 'halftime';
}

function shouldPrefer365(game: FootballGame): boolean {
  return isLiveGame(game) || game.status === 'finished';
}

async function enrich365WorldCupGames(games: FootballGame[]): Promise<FootballGame[]> {
  const detailGames = games
    .filter((game) => isLiveGame(game) || game.status === 'finished')
    .slice(0, 24);
  if (detailGames.length === 0) return games;

  const entries = await Promise.all(
    detailGames.map(async (game) => {
      try {
        const detail = await get365FootballGameDetail(game.id);
        return [game.id, detail] as const;
      } catch {
        return [game.id, null] as const;
      }
    })
  );

  const byId = new Map(
    entries.filter((entry): entry is readonly [string, FootballGame] => !!entry[1])
  );

  return games.map((game) => byId.get(game.id) ?? game);
}

function merge365Games(baseGames: FootballGame[], scores365Games: FootballGame[]): FootballGame[] {
  const merged = [...baseGames];

  for (const game365 of scores365Games) {
    const index = merged.findIndex((game) => sameFootballMatch(game, game365));
    if (index === -1) {
      merged.push(game365);
      continue;
    }

    if (shouldPrefer365(game365)) {
      merged[index] = {
        ...game365,
        league: {
          ...game365.league,
          name: game365.league.name || merged[index].league.name,
          country: merged[index].league.country || game365.league.country,
          flag: merged[index].league.flag || game365.league.flag,
        },
      };
    }
  }

  return merged;
}

export function useWorldCupGames() {
  const [games, setGames] = useState<FootballGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [source, setSource] = useState<'api-sports' | 'espn' | null>(null);

  const fetchGames = useCallback(async () => {
    try {
      let allGames: FootballGame[] = [];

      // Tenta api-sports.io (todos os jogos da Copa 2026)
      const apiData = await getWorldCupGames();
      if (apiData.length > 0) {
        allGames = apiData;
        setSource('api-sports');
      } else {
        // Fallback ESPN
        console.log('[WorldCup] api-sports.io vazio, tentando ESPN...');
        const espnData = await getWorldCupAllGames();
        allGames = espnData;
        setSource('espn');
      }

      // Se houver jogos ao vivo, mescla dados frescos de hoje (30s cache)
      // para garantir que o placar reflita o estado atual
      const hasLiveNow = allGames.some((g) => g.status === 'live' || g.status === 'halftime');
      if (hasLiveNow) {
        try {
          const todayGames = await getWorldCupTodayGames();
          if (todayGames.length > 0) {
            const todayIds = new Set(todayGames.map((g) => g.id));
            allGames = [
              ...allGames.filter((g) => !todayIds.has(g.id)),
              ...todayGames,
            ];
          }
        } catch (liveErr) {
          console.warn('[WorldCup] Falha ao buscar dados ao vivo de hoje:', liveErr);
        }
      }

      const scores365WorldCup = await get365FootballGamesList()
        .then((games) => games.filter(isWorldCupGame))
        .then(enrich365WorldCupGames)
        .catch(() => [] as FootballGame[]);
      if (scores365WorldCup.length > 0) {
        allGames = merge365Games(allGames, scores365WorldCup);
      }

      setGames(allGames);
      gameCache.setMany(allGames);
      setError(null);
      setLastUpdated(new Date());
      livePoller.notifyGameUpdates(allGames).catch(() => {});
    } catch (e: any) {
      console.error('[WorldCup] Erro api-sports:', e.message);
      try {
        const espnData = await getWorldCupAllGames();
        setGames(espnData);
        gameCache.setMany(espnData);
        setSource('espn');
        setError(null);
        setLastUpdated(new Date());
        livePoller.notifyGameUpdates(espnData).catch(() => {});
      } catch (e2: any) {
        setError(e2.message ?? 'Erro ao buscar jogos da Copa');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const hasLive = games.some((g) => g.status === 'live' || g.status === 'halftime');
  const refreshInterval = hasLive ? 30_000 : 60_000;

  useEffect(() => {
    fetchGames();
    const timer = setInterval(fetchGames, refreshInterval);
    return () => clearInterval(timer);
  }, [fetchGames, refreshInterval]);

  // Derivados
  const today = dateKeyFromDate(new Date());
  const liveGames = games.filter((g) => g.status === 'live' || g.status === 'halftime');
  const todayGames = games.filter((g) => localDateKey(g.startTime) === today);
  const upcomingGames = games.filter((g) => localDateKey(g.startTime) > today && g.status === 'scheduled');
  const finishedGames = games.filter((g) => g.status === 'finished');

  // Agrupa por data local, igual ao dia exibido no card do jogo.
  const byDay = games.reduce<Record<string, FootballGame[]>>((acc, g) => {
    const day = localDateKey(g.startTime);
    if (!acc[day]) acc[day] = [];
    acc[day].push(g);
    return acc;
  }, {});

  const days: WorldCupDay[] = Object.keys(byDay)
    .sort()
    .map((date) => ({
      date,
      label: formatDateLabel(date),
      games: byDay[date].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    }));

  return {
    games,
    liveGames,
    todayGames,
    upcomingGames,
    finishedGames,
    days,
    loading,
    error,
    refresh: fetchGames,
    lastUpdated,
    source,
  };
}
